import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getWorkOrderCounts, getWorkOrders } from "../../../../Api/WorkOrderApi";
import CODE from "../../../../Static/Constants/StatusCodes";

const DEFAULT_LIMIT = 25;

const INITIAL_QUERY = {
    // Bumped to force a refetch when nothing else about the query changed --
    // withdrawing the last row of page 1, for instance. Never sent to the
    // server.
    refresh: 0,
    // The STATUS lives IN the query, not beside it. This is the Requests /
    // History split, and the queue switches between them the way the Network
    // tab switches entity type -- so it is subject to the same rule.
    status: null,
    page: 1,
    limit: DEFAULT_LIMIT,
    searchTerm: "",
    searchBy: null,
    sortBy: null,
    sortOrder: null,
    // Panel / header selection, already mapped to request params
    // (workOrderTypeCode, createdByMe). Kept inside `query` like every other
    // narrowing input so a filter change and its page reset are one state
    // update, not two renders and two requests.
    filterParams: {},
};

// List state for the work order queue: fetch, paginate, search, sort, refetch
// after a write, and the per-status counts the tab badges show.
//
// EVERY narrowing input -- the status included -- lives in ONE state object,
// and each change writes the whole new shape in a single update. Split across
// two pieces of state, a change to one is committed in a different render than
// the reset of the other, and each commit fires a request: the first for the
// new status carrying the previous tab's page, the second with it cleared. That
// is the shape fixed in 820779fc on the Network tab, and this hook is the same
// shape on purpose -- see the note at the top of useNetworkEntities.js.
//
// The reset is done DURING RENDER rather than in an effect, which is React's
// documented way to adjust state when a prop changes: the component re-renders
// with the corrected state before anything commits, so the effect below never
// sees the stale combination at all. A skip-first-run ref is the wrong fix and
// always has been.
//
// One deliberate difference from useNetworkEntities: a status switch resets the
// PAGE but keeps the search, the sort and the filters. Every tab here renders
// the same columns off the same table config, so a search carried across still
// means something -- where a Network tab switch carried a search column the new
// entity type does not have.
export const useWorkOrders = (status) => {
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [query, setQuery] = useState({ ...INITIAL_QUERY, status: status || null });

    // Adjust-state-on-prop-change, not an effect. Bails when nothing moved, so
    // this is not a render loop.
    const nextStatus = status || null;
    if (query.status !== nextStatus) {
        setQuery((prev) => ({ ...prev, status: nextStatus, page: 1 }));
    }

    // Guards against a slow earlier response overwriting a faster later one --
    // easy to hit by typing, where the first keystroke can land after the third.
    const requestSeq = useRef(0);
    // The counts have their OWN sequence: they are a second request with a
    // different lifetime, and sharing one counter would let a counts response
    // invalidate a list response that is still the current one.
    const countsSeq = useRef(0);

    // The search/sort/filter half of the request, shared verbatim with the
    // export so the downloaded file matches the rows on screen.
    const requestArgs = useMemo(() => ({
        status: query.status || undefined,
        search: query.searchTerm || undefined,
        searchBy: query.searchBy || undefined,
        sortBy: query.sortBy || undefined,
        sortOrder: query.sortOrder || undefined,
        ...query.filterParams,
    }), [query]);

    // The same narrowing WITHOUT the status: the badges have to say what the
    // other tabs hold, so counting with the active tab's own status would
    // report zero everywhere else. The counts endpoint does not accept the
    // field at all (unknown = RAISE), so this is not optional tidiness.
    //
    // Carried as a STRING rather than as an object, because `requestArgs` gets
    // a new identity every time the page changes and the counts do not move
    // with the page -- keyed by identity, turning a page would fire a second
    // request that can only return the numbers already on screen. `refresh` is
    // folded in so a write still refreshes the badges, and stripped again
    // before the call because the schema would reject it.
    const countsKey = useMemo(() => {
        const { status: ignored, ...rest } = requestArgs;
        return JSON.stringify({ ...rest, refresh: query.refresh });
    }, [requestArgs, query.refresh]);

    const fetchList = useCallback(async () => {
        const seq = ++requestSeq.current;
        setIsLoading(true);
        setErrorMessage("");

        const res = await getWorkOrders({
            ...requestArgs,
            page: query.page,
            limit: query.limit,
        });

        // Superseded by a later request: leave every piece of state, the
        // loading flag included, to the winner. The winner always holds the
        // highest seq, so it always reaches the setIsLoading(false) below --
        // clearing the flag here instead would let a slow loser turn the
        // spinner off while the request the user is waiting on is still out.
        if (seq !== requestSeq.current) return;

        if (res?.responseStatus?.code === CODE.SUCCESS) {
            setRecords(res?.data?.records || []);
            setTotal(res?.data?.pagination?.total || 0);
        } else if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
            setRecords([]);
            setTotal(0);
            setErrorMessage("Your session has expired. Please sign in again.");
        } else {
            setRecords([]);
            setTotal(0);
            setErrorMessage(res?.responseStatus?.msg || "Could not load the list");
        }
        setIsLoading(false);
    }, [query.page, query.limit, requestArgs]);

    const fetchCounts = useCallback(async () => {
        const seq = ++countsSeq.current;
        const { refresh: ignored, ...args } = JSON.parse(countsKey);
        const res = await getWorkOrderCounts(args);
        if (seq !== countsSeq.current) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
            setCounts({
                pending: res?.data?.pending || 0,
                approved: res?.data?.approved || 0,
                rejected: res?.data?.rejected || 0,
            });
        } else {
            // Zeroed rather than left stale: a badge that keeps yesterday's
            // number beside today's list is a lie the user cannot see.
            setCounts({ pending: 0, approved: 0, rejected: 0 });
        }
    }, [countsKey]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    // Switching queues clears the rows. Keeping them showed the pending queue's
    // rows under the History tab until the response landed -- with Review
    // buttons on them, because the gate reads the row's own status. The query
    // itself is reset during render above, so this only drops the stale rows.
    useEffect(() => {
        setRecords([]);
        setTotal(0);
    }, [status]);

    const setPage = useCallback((page) => {
        setQuery((prev) => (prev.page === page ? prev : { ...prev, page }));
    }, []);

    const setLimit = useCallback((limit) => {
        setQuery((prev) =>
            prev.limit === limit ? prev : { ...prev, limit, page: 1 }
        );
    }, []);

    // page:1 in the same update as the search -- not a follow-up effect.
    // Bails when nothing changed, so re-submitting the same term is not a
    // second request.
    const applySearch = useCallback((term, by) => {
        setQuery((prev) => {
            const searchTerm = term || "";
            const searchBy = by || null;
            if (prev.searchTerm === searchTerm && prev.searchBy === searchBy) {
                return prev;
            }
            return { ...prev, searchTerm, searchBy, page: 1 };
        });
    }, []);

    const applySort = useCallback((by, order) => {
        setQuery((prev) => {
            const sortBy = by || null;
            const sortOrder = order || null;
            if (prev.sortBy === sortBy && prev.sortOrder === sortOrder) {
                return prev;
            }
            return { ...prev, sortBy, sortOrder, page: 1 };
        });
    }, []);

    // Same single-update rule as applySearch: the caller hands back its whole
    // selection, so a shallow compare of the mapped params is what decides
    // whether anything actually changed.
    const applyFilterParams = useCallback((filterParams) => {
        setQuery((prev) => {
            const next = filterParams || {};
            const prevKeys = Object.keys(prev.filterParams);
            const nextKeys = Object.keys(next);
            const unchanged =
                prevKeys.length === nextKeys.length &&
                nextKeys.every((k) => String(prev.filterParams[k]) === String(next[k]));
            return unchanged ? prev : { ...prev, filterParams: next, page: 1 };
        });
    }, []);

    // After a create, an edit, a withdrawal or a review.
    //
    // The state change IS the refetch -- calling fetchList() as well would send
    // a request built from the pre-update query, i.e. the page that just
    // emptied, followed by the corrected one. `refresh` exists so that acting
    // on page 1, where the page number does not move, still changes the query
    // and so still refetches. Both the list and the counts move, because an
    // approval takes a row out of Requests and puts it into History.
    const refetch = useCallback((rowsOnPage) => {
        setQuery((prev) => ({
            ...prev,
            page:
                rowsOnPage === 1 && prev.page > 1 ? prev.page - 1 : prev.page,
            refresh: (prev.refresh || 0) + 1,
        }));
    }, []);

    return {
        records,
        total,
        counts,
        isLoading,
        errorMessage,
        page: query.page,
        limit: query.limit,
        setPage,
        setLimit,
        applySearch,
        applySort,
        applyFilterParams,
        requestArgs,
        refetch,
    };
};

export default useWorkOrders;
